import { Component } from '@theme/component';
import { getTruck, setTruck, onTruckChange } from '@theme/dialed-truck';
import { truckLabel, kitOf } from '@theme/dialed-ymm';

/**
 * <dialed-vehicle-kits>: the kit grid on Choose your vehicle (sections/dialed-vehicle-kits.liquid).
 * Make chips and the search box filter the cards. The saved truck gets a badge and moves to the
 * front, and opening a kit saves that truck for the rest of the visit.
 */
class DialedVehicleKits extends Component {
  /** @type {Array<{handle:string,make:string,model:string,label:string}>} */
  fits = [];
  #make = '';
  #abort = new AbortController();
  #unsubscribe = () => {};

  connectedCallback() {
    super.connectedCallback();
    try {
      this.fits = JSON.parse(this.querySelector('[data-dialed-trucks]')?.textContent ?? '[]');
    } catch {
      this.fits = [];
    }
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.refs.search?.addEventListener('input', () => this.#filter(), { signal });

    // ?make=Ford opens the grid on that make
    const make = new URL(window.location.href).searchParams.get('make');
    if (make) this.#setMake(make);

    // An older truck marks the card of the kit it ships
    this.#unsubscribe = onTruckChange((truck) => this.#markMine(kitOf(this.fits.find((f) => f.handle === truck?.fit)) || truck?.fit || ''));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#unsubscribe();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const chip = target?.closest('[data-make]');
    if (chip instanceof HTMLButtonElement) {
      this.#setMake(chip.dataset.make ?? '');
      return;
    }
    const link = target?.closest('a[data-fit]');
    if (link instanceof HTMLElement) {
      const stored = getTruck();
      // A saved older truck that ships this kit stays the shopper's truck
      const mine = this.fits.find((f) => f.handle === stored?.fit);
      if (mine && mine.handle !== link.dataset.fit && kitOf(mine) === link.dataset.fit) return;
      const fit = this.fits.find((f) => f.handle === link.dataset.fit);
      if (!fit) return;
      // Keep the year already picked for this truck; the kit page picks one otherwise
      const year = stored?.fit === fit.handle ? stored.year ?? '' : '';
      setTruck({ year: year || undefined, make: fit.make, model: fit.model, fit: fit.handle, label: truckLabel(fit, year) });
    }
  };

  /** @param {string} make */
  #setMake(make) {
    const chips = [...this.querySelectorAll('button[data-make]')];
    const match = chips.find((c) => /** @type {HTMLElement} */ (c).dataset.make?.toLowerCase() === make.toLowerCase());
    this.#make = match ? /** @type {HTMLElement} */ (match).dataset.make ?? '' : '';
    for (const chip of chips) chip.setAttribute('aria-pressed', String(chip === (match ?? chips[0])));
    this.#filter();
  }

  #filter() {
    const search = this.refs.search instanceof HTMLInputElement ? this.refs.search.value : '';
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    let shown = 0;
    for (const cell of this.querySelectorAll('[data-search]')) {
      const el = /** @type {HTMLElement} */ (cell);
      const text = el.dataset.search ?? '';
      const visible = (!this.#make || el.dataset.make === this.#make) && words.every((w) => text.includes(w));
      el.hidden = !visible;
      if (visible) shown += 1;
    }
    if (this.refs.count) this.refs.count.textContent = (this.dataset.count ?? '').replace('[count]', String(shown));
    if (this.refs.none) this.refs.none.hidden = shown > 0;
  }

  /** @param {string} handle */
  #markMine(handle) {
    const grid = this.refs.grid;
    for (const cell of this.querySelectorAll('[data-search]')) {
      const el = /** @type {HTMLElement} */ (cell);
      const mine = Boolean(handle) && el.dataset.fit === handle;
      el.toggleAttribute('data-mine', mine);
      const badge = el.querySelector('.dl-vk__badge');
      if (badge instanceof HTMLElement) badge.hidden = !mine;
      if (mine && grid && grid.firstElementChild !== el) grid.prepend(el);
    }
  }
}

if (!customElements.get('dialed-vehicle-kits')) customElements.define('dialed-vehicle-kits', DialedVehicleKits);
